import { useEffect, useState } from 'react';

interface CartTimerProps {
  expiresAt: string; // ISO timestamp
  onExpire: () => void;
}

export default function CartTimer({ expiresAt, onExpire }: CartTimerProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;
      return Math.max(0, Math.floor(diff / 1000)); // seconds
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const left = calculateTimeLeft();
      setTimeLeft(left);

      if (left <= 0) {
        clearInterval(interval);
        onExpire();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Color based on time left
  const getColor = () => {
    if (timeLeft > 300) return 'text-green-600'; // > 5 min
    if (timeLeft > 120) return 'text-orange-500'; // > 2 min
    return 'text-red-600'; // < 2 min
  };

  const getProgressColor = () => {
    if (timeLeft > 300) return 'bg-green-500'; // > 5 min
    if (timeLeft > 120) return 'bg-orange-500'; // > 2 min
    return 'bg-red-500'; // < 2 min
  };

  // Calculate progress percentage (assuming 10 minutes = 600 seconds)
  const progressPercentage = Math.min(100, (timeLeft / 600) * 100);

  if (timeLeft <= 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <svg
          className="mx-auto h-8 w-8 text-red-600 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-red-800 font-semibold">¡Tiempo expirado!</p>
        <p className="text-red-600 text-sm mt-1">Tus asientos fueron liberados</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg
            className={`h-5 w-5 ${getColor()}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-700">Tiempo restante</span>
        </div>
        <span className={`text-2xl font-bold ${getColor()}`}>
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full ${getProgressColor()} transition-all duration-1000 ease-linear`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      <p className="text-xs text-gray-500 mt-2 text-center">
        {timeLeft > 120
          ? 'Completa tu compra antes de que expire'
          : '¡Apresúrate! Tu tiempo se está agotando'}
      </p>
    </div>
  );
}
